import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import { authService } from './auth.service'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        
        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface UserDetails {
    id: string
    email: string
    username: string
    first_name: string | null
    last_name: string | null
    role: 'admin' | 'user'
    is_active: boolean
    created_at: string
    updated_at: string
    last_login?: string
    total_enrollments?: number
    total_spent?: number
}

export interface UserSearchFilters {
    user_id?: string
    email?: string
    username?: string
    first_name?: string
    last_name?: string
    role?: string
    is_active?: boolean
    search_query?: string
}

export interface UserUpdateData {
    email?: string
    username?: string
    first_name?: string
    last_name?: string
    role?: 'admin' | 'user'
    is_active?: boolean
}

export interface UsersStats {
    total_users: number
    active_users: number
    admin_users: number
    total_enrollments: number
    total_revenue: number
}

// ============================================================================
// Service Class
// ============================================================================

class AdminUsersService {
    /**
     * Validate authentication token exists
     */
    private validateAuth(): void {
        if (!authService.isAuthenticated()) {
            throw new Error('AUTHENTICATION_REQUIRED: No authentication token found. Please log in.')
        }
    }

    /**
     * Handle authentication failures by logging out user
     */
    private handleAuthenticationFailure(error: any): void {
        const errorMessage = error.message || String(error)
        
        if (
            errorMessage.includes('Could not validate credentials') ||
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('Invalid token') ||
            errorMessage.includes('Token expired') ||
            errorMessage.includes('AUTHENTICATION_REQUIRED') ||
            error.response?.status === 401
        ) {
            console.error('Authentication failure detected. Logging out user...')
            
            setTimeout(() => {
                authService.logout()
            }, 1000)
            
            throw new Error('Your session has expired. Please log in again.')
        }
    }

    /**
     * Get all users with pagination
     */
    async getAllUsers(page: number = 1, pageSize: number = 50): Promise<ApiResponse<UserDetails[]>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get('/admin/users', {
                params: { page, page_size: pageSize }
            })
            
            const users = response.data?.data || response.data || []
            
            return {
                success: true,
                data: users
            }
        } catch (error: any) {
            console.error('Error fetching users:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch users'
            }
        }
    }

    /**
     * Search users with flexible filters
     */
    async searchUsers(filters: UserSearchFilters): Promise<ApiResponse<UserDetails[]>> {
        try {
            this.validateAuth()
            
            // Build query params from filters
            const params: any = {}
            if (filters.user_id) params.user_id = filters.user_id
            if (filters.email) params.email = filters.email
            if (filters.username) params.username = filters.username
            if (filters.first_name) params.first_name = filters.first_name
            if (filters.last_name) params.last_name = filters.last_name
            if (filters.role) params.role = filters.role
            if (filters.is_active !== undefined) params.is_active = filters.is_active
            if (filters.search_query) params.search_query = filters.search_query
            
            const response = await getApiInstance().get('/admin/users/search', {
                params
            })
            
            const users = response.data?.data || response.data || []
            
            return {
                success: true,
                data: users
            }
        } catch (error: any) {
            console.error('Error searching users:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to search users'
            }
        }
    }

    /**
     * Get single user details
     */
    async getUserDetails(userId: string): Promise<ApiResponse<UserDetails>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get(`/admin/users/${userId}`)
            
            const user = response.data?.data || response.data
            
            return {
                success: true,
                data: user
            }
        } catch (error: any) {
            console.error('Error fetching user details:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch user details'
            }
        }
    }

    /**
     * Update user information
     */
    async updateUser(userId: string, data: UserUpdateData): Promise<ApiResponse<UserDetails>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().patch(
                `/admin/users/${userId}`,
                data
            )
            
            const user = response.data?.data || response.data
            
            return {
                success: true,
                data: user,
                message: 'User updated successfully'
            }
        } catch (error: any) {
            console.error('Error updating user:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to update user'
            }
        }
    }

    /**
     * Delete user
     */
    async deleteUser(userId: string): Promise<ApiResponse<void>> {
        try {
            this.validateAuth()
            
            await getApiInstance().delete(`/admin/users/${userId}`)
            
            return {
                success: true,
                message: 'User deleted successfully'
            }
        } catch (error: any) {
            console.error('Error deleting user:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to delete user'
            }
        }
    }

    /**
     * Get users statistics
     */
    async getUsersStats(): Promise<ApiResponse<UsersStats>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get('/admin/users/stats')
            
            const stats = response.data?.data || response.data
            
            return {
                success: true,
                data: stats
            }
        } catch (error: any) {
            console.error('Error fetching user stats:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch user statistics'
            }
        }
    }
}

// Export singleton instance
export const adminUsersService = new AdminUsersService()
export default adminUsersService
